export type HeatmapPoint = { lat: number; lng: number; weight?: number };

type CanvasHeatmapOverlay = google.maps.OverlayView & {
  // eslint-disable-next-line no-unused-vars
  setPoints: (points: HeatmapPoint[]) => void;
  // eslint-disable-next-line no-unused-vars
  setRadius: (radius: number) => void;
  // eslint-disable-next-line no-unused-vars
  setOpacity: (opacity: number) => void;
};

const HEAT_PALETTE = [
  {
    stop: 0.0, r: 0, g: 0, b: 0, a: 0
  },
  {
    stop: 0.15, r: 0, g: 0, b: 255, a: 0.35
  },
  {
    stop: 0.35, r: 0, g: 255, b: 255, a: 0.55
  },
  {
    stop: 0.55, r: 0, g: 255, b: 0, a: 0.7
  },
  {
    stop: 0.75, r: 255, g: 255, b: 0, a: 0.85
  },
  {
    stop: 1.0, r: 255, g: 0, b: 0, a: 1
  }
];

const toNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const lerpChannel = (from: number, to: number, t: number) => Math.round(from + (to - from) * t);

const colorizeIntensity = (intensity: number): [number, number, number, number] => {
  const t = Math.max(0, Math.min(1, intensity / 255));
  if (t <= 0) return [0, 0, 0, 0];

  let lower = HEAT_PALETTE[0];
  let upper = HEAT_PALETTE[HEAT_PALETTE.length - 1];

  for (let i = 0; i < HEAT_PALETTE.length - 1; i += 1) {
    if (t >= HEAT_PALETTE[i].stop && t <= HEAT_PALETTE[i + 1].stop) {
      lower = HEAT_PALETTE[i];
      upper = HEAT_PALETTE[i + 1];
      break;
    }
  }

  const range = upper.stop - lower.stop || 1;
  const localT = (t - lower.stop) / range;

  return [
    lerpChannel(lower.r, upper.r, localT),
    lerpChannel(lower.g, upper.g, localT),
    lerpChannel(lower.b, upper.b, localT),
    Math.round(lerpChannel(lower.a * 255, upper.a * 255, localT))
  ];
};

export const parseHeatmapRows = (rows: unknown): HeatmapPoint[] => {
  if (!Array.isArray(rows)) return [];

  const points: HeatmapPoint[] = [];

  rows.forEach((row: any) => {
    const lat = toNumber(
      row?.originLatitude
      ?? row?.originlatitude
      ?? row?.origin_latitude
      ?? row?.latitude
      ?? row?.lat
      ?? row?.origin?.latitude
      ?? row?.origin?.lat
    );
    const lng = toNumber(
      row?.originLongitude
      ?? row?.originlongitude
      ?? row?.origin_longitude
      ?? row?.longitude
      ?? row?.lng
      ?? row?.origin?.longitude
      ?? row?.origin?.lng
    );
    const weight = toNumber(row?.weight ?? row?.count ?? row?.qty) ?? 1;

    if (lat == null || lng == null || lat === 0 || lng === 0) {
      return;
    }

    points.push({ lat, lng, weight });
  });

  return points;
};

/**
 * Canvas-based heatmap overlay (classic density style).
 * Pass 1 accumulates intensity; pass 2 colorizes into a continuous heat field.
 */
export const createCanvasHeatmapOverlay = (
  map: google.maps.Map,
  initialPoints: HeatmapPoint[] = [],
  options?: { radius?: number; opacity?: number }
): CanvasHeatmapOverlay => {
  const overlay = new google.maps.OverlayView() as CanvasHeatmapOverlay;
  let canvas: HTMLCanvasElement | null = null;
  let intensityCanvas: HTMLCanvasElement | null = null;
  let points = [...initialPoints];
  let radius = options?.radius ?? 56;
  let opacity = options?.opacity ?? 0.5;

  const draw = () => {
    if (!canvas) return;
    const projection = overlay.getProjection();
    if (!projection) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
    const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
    if (!sw || !ne) return;

    const width = Math.max(1, Math.ceil(ne.x - sw.x));
    const height = Math.max(1, Math.ceil(sw.y - ne.y));
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.floor(width * ratio);
    const pixelHeight = Math.floor(height * ratio);

    canvas.style.left = `${sw.x}px`;
    canvas.style.top = `${ne.y}px`;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;

    if (!intensityCanvas) {
      intensityCanvas = document.createElement('canvas');
    }
    intensityCanvas.width = pixelWidth;
    intensityCanvas.height = pixelHeight;

    const intensityCtx = intensityCanvas.getContext('2d', { willReadFrequently: true });
    const ctx = canvas.getContext('2d');
    if (!intensityCtx || !ctx) return;

    intensityCtx.setTransform(1, 0, 0, 1, 0, 0);
    intensityCtx.clearRect(0, 0, pixelWidth, pixelHeight);
    intensityCtx.filter = 'none';
    intensityCtx.globalCompositeOperation = 'lighter';

    const scaledRadius = radius * ratio;
    points.forEach((point) => {
      const pixel = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(point.lat, point.lng)
      );
      if (!pixel) return;

      const x = (pixel.x - sw.x) * ratio;
      const y = (pixel.y - ne.y) * ratio;
      if (
        x < -scaledRadius
        || y < -scaledRadius
        || x > pixelWidth + scaledRadius
        || y > pixelHeight + scaledRadius
      ) {
        return;
      }

      const weight = Math.max(0.6, Math.min(point.weight ?? 1, 4));
      const pointRadius = scaledRadius * Math.sqrt(weight);
      const gradient = intensityCtx.createRadialGradient(x, y, 0, x, y, pointRadius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
      gradient.addColorStop(0.45, 'rgba(0, 0, 0, 0.2)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      intensityCtx.beginPath();
      intensityCtx.fillStyle = gradient;
      intensityCtx.arc(x, y, pointRadius, 0, Math.PI * 2);
      intensityCtx.fill();
    });

    // Soft blur so sparse points melt into continuous heat clouds.
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = pixelWidth;
    blurCanvas.height = pixelHeight;
    const blurCtx = blurCanvas.getContext('2d');
    if (blurCtx) {
      blurCtx.filter = `blur(${Math.max(10, scaledRadius * 0.22)}px)`;
      blurCtx.drawImage(intensityCanvas, 0, 0);
      intensityCtx.globalCompositeOperation = 'source-over';
      intensityCtx.clearRect(0, 0, pixelWidth, pixelHeight);
      intensityCtx.filter = 'none';
      intensityCtx.drawImage(blurCanvas, 0, 0);
    }

    const image = intensityCtx.getImageData(0, 0, pixelWidth, pixelHeight);
    const { data } = image;
    for (let i = 0; i < data.length; i += 4) {
      const intensity = data[i + 3];
      if (intensity === 0) {
        data[i] = 0;
        data[i + 1] = 0;
        data[i + 2] = 0;
        data[i + 3] = 0;
      } else {
        const [r, g, b, a] = colorizeIntensity(intensity);
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = a;
      }
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pixelWidth, pixelHeight);
    ctx.putImageData(image, 0, 0);
  };

  overlay.onAdd = () => {
    canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';
    canvas.style.opacity = String(opacity);
    canvas.style.zIndex = '1';

    const panes = overlay.getPanes();
    (panes?.floatPane ?? panes?.overlayMouseTarget ?? panes?.overlayLayer)?.appendChild(canvas);
  };

  overlay.draw = () => {
    draw();
  };

  overlay.onRemove = () => {
    canvas?.parentNode?.removeChild(canvas);
    canvas = null;
    intensityCanvas = null;
  };

  overlay.setPoints = (nextPoints: HeatmapPoint[]) => {
    points = [...nextPoints];
    draw();
  };

  overlay.setRadius = (nextRadius: number) => {
    radius = nextRadius;
    draw();
  };

  overlay.setOpacity = (nextOpacity: number) => {
    opacity = nextOpacity;
    if (canvas) canvas.style.opacity = String(opacity);
  };

  overlay.setMap(map);
  return overlay;
};
